/* ═══════════════════════════════════════════════════════════════
   The Designer — toolbar controls that follow the caret.

   Level, inline emphasis and undo/redo all read editor state, which
   changes on every keystroke. Each one subscribes to the editor
   itself so a keystroke re-renders a 32px button — never the whole
   workspace, and never the docked palettes.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Editor } from '@tiptap/react';
import { Bold, Highlighter, Italic, Redo2, Underline, Undo2 } from 'lucide-react';
import { BLOCK_NODE_NAMES, changeLevel } from './commands';
import type { UndoCoordinator } from './undoCoordinator';

/** Re-render on every editor transaction. */
function useEditorTick(editor: Editor | null) {
  const [, force] = React.useReducer((x: number) => x + 1, 0);
  React.useEffect(() => {
    if (!editor) return;
    editor.on('transaction', force);
    return () => {
      editor.off('transaction', force);
    };
  }, [editor]);
}

/** The level of the block the caret sits in, or null. */
export function caretLevelOf(editor: Editor | null): number | null {
  if (!editor) return null;
  const { $from } = editor.state.selection;
  for (let d = $from.depth; d >= 0; d--) {
    const node = $from.node(d);
    if (BLOCK_NODE_NAMES.includes(node.type.name)) {
      return (node.attrs.level as number) || null;
    }
  }
  return null;
}

function IconButton({
  label,
  onClick,
  disabled,
  pressed,
  title,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  pressed?: boolean;
  /** Hover text, when it should say more than the accessible name does. */
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={`dsg-icon-btn${pressed ? ' dsg-tool-on' : ''}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
      title={title ?? label}
    >
      {children}
    </button>
  );
}

/* ─── Level: promote / read / demote ───
   Changing a paragraph's level is THE core operation, so it is visible and
   clickable — never Tab-only (DESIGN §1: the user is an architect,
   mouse-first; nothing keyboard-only). */

export function LevelCluster({
  editor,
  canEdit,
  onLevelStyle,
}: {
  editor: Editor | null;
  canEdit: boolean;
  /** Open the Levels palette on the caret's level. */
  onLevelStyle?: (level: number) => void;
}) {
  useEditorTick(editor);
  const level = caretLevelOf(editor);
  const shift = (delta: number) => {
    if (!editor) return;
    changeLevel(editor.state, editor.view.dispatch.bind(editor.view), delta);
    editor.commands.focus();
  };
  return (
    <div className="dsg-group" role="group" aria-label="Paragraph level">
      <IconButton
        label="Promote paragraph (Shift+Tab)"
        onClick={() => shift(-1)}
        disabled={!editor || !canEdit || level === null || level <= 1}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M11 5h10M11 12h10M11 19h10M7 9l-4 3 4 3" />
        </svg>
      </IconButton>
      <button
        type="button"
        className="dsg-level-value"
        aria-live="polite"
        disabled={!editor || level === null || !onLevelStyle}
        onClick={() => level !== null && onLevelStyle?.(level)}
        title={
          level === null
            ? 'Put the caret in a paragraph to see its level'
            : `Level ${level} — click to style every level ${level} paragraph`
        }
      >
        {level === null ? 'Level —' : `Level ${level}`}
      </button>
      <IconButton
        label="Demote paragraph (Tab)"
        onClick={() => shift(+1)}
        disabled={!editor || !canEdit || level === null || level >= 9}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M11 5h10M11 12h10M11 19h10M3 9l4 3-4 3" />
        </svg>
      </IconButton>
    </div>
  );
}

/* ─── Inline text controls (Word basics) ───
   Direct formatting, additive over the level style, and a different thing
   from revision marks — which is exactly why the highlighter icon belongs
   HERE and Track Changes gets a change-bar glyph of its own. */

export function InlineMarks({ editor, canEdit }: { editor: Editor | null; canEdit: boolean }) {
  useEditorTick(editor);
  const toggle = (name: string) => editor?.chain().focus().toggleMark(name).run();
  const on = (name: string) => !!editor?.isActive(name);
  return (
    <div className="dsg-group" role="group" aria-label="Text formatting">
      <IconButton
        label="Bold (Ctrl+B)"
        pressed={on('bold')}
        disabled={!editor || !canEdit}
        onClick={() => toggle('bold')}
      >
        <Bold size={15} aria-hidden="true" />
      </IconButton>
      <IconButton
        label="Italic (Ctrl+I)"
        pressed={on('italic')}
        disabled={!editor || !canEdit}
        onClick={() => toggle('italic')}
      >
        <Italic size={15} aria-hidden="true" />
      </IconButton>
      <IconButton
        label="Underline (Ctrl+U)"
        pressed={on('underline')}
        disabled={!editor || !canEdit}
        onClick={() => toggle('underline')}
      >
        <Underline size={15} aria-hidden="true" />
      </IconButton>
      <IconButton
        label="Highlight (Ctrl+Alt+H)"
        pressed={on('highlight')}
        disabled={!editor || !canEdit}
        onClick={() => toggle('highlight')}
      >
        <Highlighter size={15} aria-hidden="true" />
      </IconButton>
    </div>
  );
}

/* ─── Undo / redo — UNIVERSAL (contract §2.7) ───
   One pair of buttons for the whole surface. They undo the LAST THING THE
   USER DID, whether that was typing a sentence, dragging a token into the
   footer, or restyling a level. The user never picks a "kind" of undo. */

export function UniversalHistory({
  timeline,
  editor,
}: {
  timeline: UndoCoordinator;
  editor: Editor | null;
}) {
  // The editor's own transactions change what is undoable, so this cluster
  // still follows the caret — it just no longer speaks only for the text.
  useEditorTick(editor);
  return (
    <div className="dsg-group" role="group" aria-label="Undo and redo">
      <IconButton
        label="Undo (Ctrl+Z)"
        onClick={timeline.undo}
        disabled={!timeline.canUndo}
        title="Undo the last thing you did — text or formatting"
      >
        <Undo2 size={15} aria-hidden="true" />
      </IconButton>
      <IconButton
        label="Redo (Ctrl+Y)"
        onClick={timeline.redo}
        disabled={!timeline.canRedo}
        title="Redo the last thing you undid"
      >
        <Redo2 size={15} aria-hidden="true" />
      </IconButton>
    </div>
  );
}
